import * as React from 'react'

import { format as formatDate } from 'date-fns'

import { Calendar } from '~/client/src/shared/components/shadcn-ui/calendar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~/client/src/shared/components/shadcn-ui/popover'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'
import { capitalizeEachWord } from '~/client/src/shared/utils/capitalizeText'
import { cn } from '~/client/src/shared/utils/cn'

const YEAR_RANGE = 10

// `text-base` on mobile / `text-sm` on desktop mirrors the Input primitive so
// the date field reads at the same size as the time field on both platforms.
const TRIGGER =
  'tw:flex tw:h-8 tw:w-full tw:cursor-pointer tw:items-center tw:rounded-lg tw:border tw:border-input tw:bg-transparent tw:px-2.5 tw:text-base tw:outline-none tw:focus-visible:border-ring tw:focus-visible:ring-3 tw:focus-visible:ring-ring/50 tw:md:text-sm'

interface IProps {
  value: Date | null
  onChange: (date: Date) => void
  projectDateStore: ProjectDateStore
  placeholder?: string
  /** Portal target for the calendar popup; defaults to `document.body` when omitted. */
  portalContainer?: React.ComponentProps<
    typeof PopoverContent
  >['portalContainer']
}

function DateField({
  value,
  onChange,
  projectDateStore,
  placeholder,
  portalContainer,
}: IProps) {
  const [isOpen, setIsOpen] = React.useState(false)

  const currentYear = new Date().getFullYear()
  const startMonth = new Date(currentYear - YEAR_RANGE, 0)
  const endMonth = new Date(currentYear + YEAR_RANGE, 11)

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger render={<button type="button" className={TRIGGER} />}>
        <span className={cn(!value && 'tw:text-muted-foreground')}>
          {/* The calendar deals in days, not instants: it hands back local midnight
              for the cell that was clicked, so the label is read off that same clock.
              Formatting it in the project's timezone would name the neighbouring day
              for a viewer far enough from the project, disagreeing with the cell the
              calendar highlights. The caller anchors the day to project time when it
              combines it with a time. */}
          {value
            ? capitalizeEachWord(
                formatDate(
                  value,
                  projectDateStore.currentFormat.monthDayAndYear,
                  {
                    locale: Localization.dateLocale,
                  },
                ),
              )
            : placeholder}
        </span>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="tw:w-auto tw:p-0"
        portalContainer={portalContainer}
      >
        <Calendar
          mode="single"
          captionLayout="dropdown"
          selected={value ?? undefined}
          defaultMonth={value ?? undefined}
          startMonth={startMonth}
          endMonth={endMonth}
          onSelect={date => {
            if (!date) return
            onChange(date)
            setIsOpen(false)
          }}
        />
      </PopoverContent>
    </Popover>
  )
}

export default DateField
